import React, { Component, ErrorInfo, ReactNode } from 'react'
import { ServiceType } from '../../types'
import { ServiceErrorFallback } from './ServiceErrorFallback'

interface Props {
  children: ReactNode
  serviceType?: ServiceType
  fallback?: React.ComponentType<ErrorFallbackProps>
}

interface State {
  hasError: boolean
  error?: Error
  errorInfo?: ErrorInfo
}

export interface ErrorFallbackProps {
  error?: Error
  errorInfo?: ErrorInfo
  serviceType?: ServiceType
  resetError: () => void
}

export class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props)
    this.state = { hasError: false }
  }

  static getDerivedStateFromError(error: Error): State {
    return {
      hasError: true,
      error
    }
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    this.setState({
      error,
      errorInfo
    })

    // Log error to monitoring service
    this.logError(error, errorInfo)
  }

  private logError = (error: Error, errorInfo: ErrorInfo) => {
    const errorData = {
      message: error.message,
      stack: error.stack,
      componentStack: errorInfo.componentStack,
      serviceType: this.props.serviceType,
      timestamp: new Date().toISOString(),
      userAgent: navigator.userAgent,
      url: window.location.href
    }

    // In production, send to error monitoring service
    if (import.meta.env.PROD) {
      // Example: Send to Sentry, LogRocket, or custom error service
      console.error('Error Boundary caught an error:', errorData)
    } else {
      console.error('Error Boundary caught an error:', errorData)
    }
  }

  private resetError = () => {
    this.setState({ hasError: false, error: undefined, errorInfo: undefined })
  }

  render() {
    if (this.state.hasError) {
      const FallbackComponent = this.props.fallback || ServiceErrorFallback
      
      return (
        <FallbackComponent
          error={this.state.error}
          errorInfo={this.state.errorInfo}
          serviceType={this.props.serviceType}
          resetError={this.resetError}
        />
      )
    }

    return this.props.children
  }
}